import { useCallback, useEffect } from 'react';
import { CommonService } from '@/services';
import commonService from '@/services/common.service.ts';
import { useSocket } from "@/services/hooks/useSocket.ts";

enum PMEventType {
    PM_AGENT_RESPONSE = "pm_agent.response",
    PM_AGENT_DELTA = "description_delta",
    PM_AGENT_ERROR = "pm_agent.error",
    PM_AGENT_COMPLETE = "pm_agent.complete",
}

enum SocketErrorEventType {
    SOCKET_ERROR = "socket.error",
}

export const usePmSocket = (
    updateLoadingMessage: (message: any, isStreaming?: boolean) => void, 
    projectId: string, 
    handleErrorMessage: (errorMessage?: string) => void
) => {

    const { events } = useSocket({
        eventTypes: [
            PMEventType.PM_AGENT_RESPONSE,
            PMEventType.PM_AGENT_DELTA,
            PMEventType.PM_AGENT_ERROR,
            PMEventType.PM_AGENT_COMPLETE,
            SocketErrorEventType.SOCKET_ERROR,
        ],
        autoConnect: true,
        persist: true,
        projectId: projectId || "",
        logTag: 'PmAgentSocket',
        debug: false, // Set to true only during development for detailed logs
    });

    const handlePmAgentEvent = useCallback((event_type: string, data: any) => {
        
        switch (event_type) {
            case PMEventType.PM_AGENT_RESPONSE: {
                // Handle complete PM agent response
                updateLoadingMessage({
                    message: data?.response || data?.message || '',
                    extra: {
                        model_usage: data?.model_usage,
                        session_id: data?.session_id,
                    }
                }, false);
                break;
            }
                
            case PMEventType.PM_AGENT_DELTA: {
                // Handle streaming PM agent response
                updateLoadingMessage({
                    message: data?.delta || data?.message || '',
                    extra: {
                        session_id: data?.session_id,
                        is_streaming: true,
                    }
                }, true);
                break;
            }
                
            case PMEventType.PM_AGENT_ERROR: {
                // Handle PM agent specific errors
                const errorMessage = data?.error || data?.message || 'PM Agent encountered an error';
                handleErrorMessage(errorMessage);
                break;
            }
                
            case PMEventType.PM_AGENT_COMPLETE: {
                // Handle completion of PM agent response
                updateLoadingMessage({
                    extra: {
                        is_streaming: false,
                        session_id: data?.session_id,
                        completion_time: data?.completion_time,
                    }
                }, false);
                break;
            }
                
            default:
                console.warn(`[PM Socket] Unknown PM event type: ${event_type}`);
        }
    }, [updateLoadingMessage, handleErrorMessage]);

    const handleStreamFromSocket = useCallback((event_type: string, data: any) => {
        switch (event_type) {
            case PMEventType.PM_AGENT_DELTA:
                updateLoadingMessage({
                    message: data?.delta || data?.message || '',
                    extra: {
                        session_id: data?.session_id,
                        is_streaming: true,
                    }
                }, true);
                break;
                
            case PMEventType.PM_AGENT_RESPONSE:
                updateLoadingMessage({
                    message: data?.response || data?.message || '',
                    extra: {
                        model_usage: data?.model_usage,
                        session_id: data?.session_id,
                    }
                }, false);
                break;
                
            default:
                console.warn(`[PM Socket] Unknown PM stream event: ${event_type}`);
        }
    }, [updateLoadingMessage]);

    // Process RxJS stream chunks for PM agent
    const processChunk = useCallback((chunk: string) => {
        try {
            const message = commonService.processStream(chunk);

            const eventType = message?.type;
            const eventData = message?.data;

            if (eventType) {
                handlePmAgentEvent(eventType, eventData);
            }
        } catch (err) {
            console.error("[PM Socket] Error processing chunk:", chunk, err);
        }
    }, [handlePmAgentEvent]);

    // Handle socket events
    useEffect(() => {
        const pmAgentResponseEvent = events[PMEventType.PM_AGENT_RESPONSE];
        const pmAgentDeltaEvent = events[PMEventType.PM_AGENT_DELTA];
        const pmAgentErrorEvent = events[PMEventType.PM_AGENT_ERROR];
        const pmAgentCompleteEvent = events[PMEventType.PM_AGENT_COMPLETE];

        if (pmAgentResponseEvent) {
            handleStreamFromSocket(PMEventType.PM_AGENT_RESPONSE, pmAgentResponseEvent?.event_meta);
        } else if (pmAgentDeltaEvent) {
            handleStreamFromSocket(PMEventType.PM_AGENT_DELTA, pmAgentDeltaEvent?.event_meta);
        } else if (pmAgentErrorEvent) {
            handlePmAgentEvent(PMEventType.PM_AGENT_ERROR, pmAgentErrorEvent?.event_meta);
        } else if (pmAgentCompleteEvent) {
            handlePmAgentEvent(PMEventType.PM_AGENT_COMPLETE, pmAgentCompleteEvent?.event_meta);
        }
    }, [events, handleStreamFromSocket, handlePmAgentEvent]);

    // Handle socket errors
    useEffect(() => {
        const errorEvent = events[SocketErrorEventType.SOCKET_ERROR];
        if (errorEvent) {
            const reason = errorEvent.event_meta?.extra?.reason || errorEvent.event_meta?.reason;
            if (reason) {
                handleErrorMessage(`Socket error: ${reason}`);
            }
        }
    }, [events, handleErrorMessage]);

    // RxJS Subscriptions for PM agent specific streaming
    useEffect(() => {
        const sub = CommonService._communications.handlePmResponseStreamerSubject.subscribe(processChunk);
        return () => sub.unsubscribe();
    }, [processChunk]);

    return {
        // Return any additional PM socket functionality if needed
        isConnected: true, // This would come from the socket service
    };
};
